import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router";

export type LabelGuardReason = "dirty";

interface UseLabelNavigationGuardOptions {
  isDirty: boolean;
  isSaving: boolean;
}

/**
 * The label page's navigation guard: unsaved annotations, and nothing else.
 *
 * It used to also lock the page for the whole of an auto-annotation run,
 * because the boxes existed only in the HTTP response — a labeller who
 * switched images lost the run, and a late reply had nowhere safe to land. A
 * completed run now stages its boxes server-side, so leaving costs nothing:
 * the results are waiting on the image when they come back.
 *
 * Blocks `?task=` changes as well as pathname changes: switching tasks via
 * DataSourcePanel / pagination / arrow keys is a search-param navigation that
 * would otherwise silently overwrite local edits once the new task's detail
 * query resolves.
 */
export const useLabelNavigationGuard = ({
  isDirty,
  isSaving,
}: UseLabelNavigationGuardOptions) => {
  const dirtyBlocks = isDirty && !isSaving;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!dirtyBlocks) return false;
    if (currentLocation.pathname !== nextLocation.pathname) return true;
    const currentTask = new URLSearchParams(currentLocation.search).get("task");
    const nextTask = new URLSearchParams(nextLocation.search).get("task");
    return currentTask !== nextTask;
  });

  useEffect(() => {
    if (!dirtyBlocks) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirtyBlocks]);

  const blocked = blocker.state === "blocked";

  // Tests `isDirty`, not `dirtyBlocks`: a save started while the dialog is
  // already open must keep it open rather than fall through to the
  // auto-release below and navigate away mid-save.
  const reason: LabelGuardReason | null = blocked && isDirty ? "dirty" : null;

  // A blocked navigation whose reason evaporated would otherwise stay blocked
  // forever with no dialog on screen to release it.
  useEffect(() => {
    if (blocked && reason === null) blocker.proceed?.();
  }, [blocked, reason, blocker]);

  const proceed = useCallback(() => blocker.proceed?.(), [blocker]);
  const reset = useCallback(() => blocker.reset?.(), [blocker]);

  return { reason, proceed, reset };
};
